import Client from 'braintree-web/client'
import ThreeDSecure from 'braintree-web/three-d-secure'
import { mapCardTypeToServer } from './utils'
import BetterError from '../../utils/betterError'
import { apiCall } from '../api'

// Example https://developers.braintreepayments.com/guides/3d-secure/client-side/javascript/v3
// https://braintree.github.io/braintree-web/current/module-braintree-web_three-d-secure.html#create-examples
export const threeDSCheck = async (client, nonce, creditCardData, bin) => {
  // Braintree doesn't have a promise 3ds version
  return new Promise((resolve, reject) => {
    ThreeDSecure.create(
      {
        client,
        version: 2,
      },
      (createError, threeDSecureInstance) => {
        if (createError) {
          reject(createError)
        }

        // Lookup happend before modal shows
        // Don't understand why exaclty we should write it, but sdk shows error without that call
        threeDSecureInstance.on('lookup-complete', (_, next) => {
          next()
        })

        threeDSecureInstance.verifyCard(
          {
            amount: '0', // Yes, braintree needs a string
            nonce,
            bin,
            billingAddress: creditCardData.billingAddress,
          },
          (verifyError, payload) => {
            if (verifyError || (payload.liabilityShiftPossible && !payload.liabilityShifted)) {
              reject(
                new BetterError('Unsuccessful card verification, please try again', {
                  error: verifyError,
                })
              )
            }

            resolve(payload)
          }
        )
      }
    )
  })
}

let braintreeClient = null
/**
 * Init braintree client
 * @param {string} token
 * @returns {Promise<BraintreeInstance|Error>} BraintreeInstance
 */
const initBraintreeByServerToken = async token => {
  try {
    // Prevent double initialization
    if (!braintreeClient) {
      braintreeClient = await Client.create({ authorization: token })
    }

    return braintreeClient
  } catch (error) {
    braintreeClient = null
    throw error
  }
}

/**
 * @typedef BraintreeResponse
 * @property {Array} errors Array of errors per field
 * @property {string} nonce Nonce from tokenization
 * @property {string} bin Extra info from braintree
 */

/**
 * Get nonce for credit card by endpoint, process errors if exist
 * @param {BraintreeInstance} client Braitree instance
 * @param {Object} data Credit card data
 * @param {bool} need3ds With verification
 * @returns {Promise<BraintreeResponse|Error>} Object with nonce or errors
 */
const getNonceFromBraintree = async (client, data, need3ds = false) => {
  try {
    // https://braintree.github.io/braintree-web/current/Client.html#request-examples
    const result = await client.request({
      endpoint: 'payment_methods/credit_cards',
      method: 'post',
      data: {
        creditCard: {
          ...data,
          // We should explicit verificate card if needed
          // but now that future turned off
          // app7serverside chaneel thread https://jetsmarter.slack.com/archives/GR3SZ7FRV/p1617128321000600
          options: { validate: need3ds },
        },
      },
    })
    if (!result || !result.creditCards || !result.creditCards[0].nonce) {
      throw new Error('Braintree tokenization unsuccessful')
    }

    const cardInfo = result.creditCards[0]

    return {
      nonce: cardInfo.nonce,
      bin: cardInfo.details?.bin,
    }
  } catch (error) {
    // NOTE: collect the errors from braintree endpoint
    const errorsPerFields = error.details?.originalError?.fieldErrors[0]?.fieldErrors
    if (errorsPerFields) {
      return {
        errors: errorsPerFields.map(({ field, message }) => ({ field, message })),
      }
    }

    // If we dont have errors per field - throw original error
    throw error
  }
}

/**
 * Decorator for getNonceFromBraintree with 3DS secure
 * That func throw error without handle it, because we will catch it above
 * @param {Object} creditCardData
 * @returns {Promise<BraintreeResponse|Error>} Object with nonce or errors
 */
export const getNonce = async ({
  number,
  expirationDate,
  cvv,
  postalCode,
  cardHolder,
  cardType,
}) => {
  const creditCardData = {
    cardholderName: cardHolder,
    number,
    expirationDate,
    cvv,
    ...postalCode && {
      billingAddress: {
        postalCode,
      }
    },
  }

  // 1. Get payment token and 3ds restrictions from server
  const isSlateCard = window.location.search.includes('slate=1')
  const { need3ds, token: tokenFromOurServer } = await apiCall('getPaymentToken', {
    cardType: mapCardTypeToServer(cardType),
    requestProcessor: isSlateCard ? 'slate' : 'xo',
  })

  // 2. Init braintree client by token
  const client = await initBraintreeByServerToken(tokenFromOurServer)

  // 3. Send card to braintree, errors or nonce/bin should been returned
  let { nonce, bin, errors } = await getNonceFromBraintree(client, creditCardData)
  if (errors) {
    return { errors }
  }

  // 4. Start 3ds if needed, rewrite nonce
  if (need3ds) {
    const threeDSResponse = await threeDSCheck(client, nonce, creditCardData, bin)
    // eslint-disable-next-line prefer-destructuring
    nonce = threeDSResponse.nonce
  }

  return { token: nonce }
}
